import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi, sellersApi } from '../../services/apiService';
import { API_BASE_URL } from '../../config/api';
import MobileBottomNav from '../../components/MobileBottomNav/MobileBottomNav';
import './MobileAIModePage.css';

// ─── AI brain: pattern-match against real DB data ───────────────────────────
function getAIResponse(query, products, sellers) {
  try {
  const q = (query || '').toLowerCase().trim();
  if (!q) return { type: 'text', text: 'Please describe what you are looking for.' };

  // ── keyword helpers ── safe for any value type
  const toStr = (v) => (v != null ? String(v) : '').toLowerCase();
  const matchesAny = (str, kws) => { const s = toStr(str); return kws.some(k => s.includes(k)); };

  const safeProducts = Array.isArray(products) ? products : [];
  const safeSellers = Array.isArray(sellers) ? sellers : [];

  // find products matching keywords
  const findProducts = (kws) =>
    safeProducts.filter(p => matchesAny(p.name, kws) || matchesAny(p.description, kws) || matchesAny(p.category, kws));

  // find sellers matching keywords
  const findSellers = (kws) =>
    safeSellers.filter(s => matchesAny(s.companyName || s.name, kws) || matchesAny(s.description, kws) || matchesAny(s.country, kws));

  // generic keyword extract from query
  const keywords = q.split(/\s+/).filter(w => w.length > 2 && !['the','and','for','with','can','you','show','find','get','what','how'].includes(w));

  // ── Intent detection ──

  // Verified manufacturer search
  if (matchesAny(q, ['manufacturer','supplier','factory','producer','vendor','verified'])) {
    const kws = keywords.length ? keywords : [''];
    const matched = findSellers(kws).slice(0, 5);
    if (matched.length) {
      return {
        type: 'sellers',
        text: `Found **${matched.length} verified manufacturer${matched.length > 1 ? 's' : ''}** matching your needs:`,
        items: matched,
      };
    }
    return { type: 'text', text: `I searched our verified manufacturer database but couldn't find an exact match for "${query}". Try broader terms like "steel", "textile", or "electronics".` };
  }

  // Trending / bestsellers
  if (matchesAny(q, ['trending','bestseller','best seller','popular','hot','top','most sold','demand'])) {
    const sorted = [...products].sort((a, b) => (b.soldCount || b.views || 0) - (a.soldCount || a.views || 0)).slice(0, 6);
    return {
      type: 'products',
      text: `Here are the **trending products** right now based on our platform data:`,
      items: sorted,
    };
  }

  // Market potential / evaluate
  if (matchesAny(q, ['market','potential','analyze','insight','evaluate','opportunity','size','growth'])) {
    const relProducts = findProducts(keywords).slice(0, 4);
    const relSellers = findSellers(keywords).slice(0, 3);
    const totalProd = relProducts.length;
    const totalSell = relSellers.length;
    const categories = [...new Set(products.map(p => p.category).filter(Boolean))].slice(0, 5);
    return {
      type: 'market',
      text: `**Market Analysis for "${query}"**`,
      stats: [
        { label: 'Products available', value: totalProd || products.length },
        { label: 'Active suppliers', value: totalSell || sellers.length },
        { label: 'Top categories', value: categories.join(', ') || 'Various' },
        { label: 'Sourcing platform', value: 'GCC Constructor' },
      ],
      products: relProducts,
    };
  }

  // Design / AI design suggestions
  if (matchesAny(q, ['design','create','generate','idea','suggest','blueprint','prototype','style'])) {
    const relProducts = findProducts(keywords).slice(0, 4);
    return {
      type: 'design',
      text: `**Design Suggestions for "${query}"**\n\nBased on our product catalog, here are some design options and sourcing leads:`,
      ideas: [
        `Modular design with customizable dimensions`,
        `Eco-friendly materials from verified suppliers`,
        `High-volume production ready factories`,
        `Custom branding & OEM available`,
      ],
      products: relProducts,
    };
  }

  // Discover trends
  if (matchesAny(q, ['discover','trend','what\'s new','latest','new arrival','recent','category'])) {
    const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
    const trending = categories.slice(0, 6);
    return {
      type: 'trends',
      text: `**Trending Categories on our Platform:**`,
      categories: trending,
      products: products.slice(0, 4),
    };
  }

  // Product search (default)
  const matched = findProducts(keywords);
  if (matched.length > 0) {
    return {
      type: 'products',
      text: `Found **${matched.length} product${matched.length > 1 ? 's' : ''}** matching "${query}":`,
      items: matched.slice(0, 6),
    };
  }

  // Fallback
  return {
    type: 'text',
    text: `I couldn't find specific results for "${query}" in our database. Try searching for product categories like "steel pipes", "ceramic tiles", or "aluminum profiles". You can also use the feature buttons below to explore.`,
  };
  } catch (err) {
    console.error('[AI] getAIResponse error:', err);
    return { type: 'text', text: 'I had trouble processing that request. Please try again with a different phrase.' };
  }
}

// ─── Suggested prompts (shown on landing) ───────────────────────────────────
const SUGGESTED = [
  { id: 'sup1', title: 'Your Next Best Supplier Is Here', sub: 'Source custom jewelry factory' },
  { id: 'sup2', title: "See What's Trending in Your Market", sub: 'What makes the bestselling fashion rings a customer fa...' },
];
const CHIPS = [
  'Verified manufacturer search',
  'Design with AI',
  'Product search',
  'Analyze bestsellers',
  'Evaluate market potential',
  'Discover trends',
];

const CHIP_QUERIES = {
  'Verified manufacturer search': 'Show me verified manufacturers',
  'Design with AI': 'Design suggestions for modern products',
  'Product search': 'Show me top products',
  'Analyze bestsellers': 'What are the trending bestsellers',
  'Evaluate market potential': 'Evaluate market potential for construction materials',
  'Discover trends': 'Discover the latest trends',
};

// ─── Product card ────────────────────────────────────────────────────────────
function ProductCard({ product, navigate }) {
  const imgSrc = product.image
    ? (product.image.startsWith('http') ? product.image : `${API_BASE_URL.replace('/api', '')}${product.image}`)
    : 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&auto=format&fit=crop';
  return (
    <div className="mai-card" onClick={() => navigate(`/mobile/product/${product.id}`)}>
      <div className="mai-card-img">
        <img src={imgSrc} alt={product.name} loading="lazy" />
      </div>
      <div className="mai-card-body">
        <p className="mai-card-name">{product.name}</p>
        {(product.priceMin || product.price) && (
          <p className="mai-card-price">
            ${product.priceMin || product.price}
            {product.priceMax && ` – $${product.priceMax}`}
          </p>
        )}
        {product.moq && <p className="mai-card-moq">MOQ: {product.moq}</p>}
        {product.country && <p className="mai-card-country">{product.country}</p>}
      </div>
    </div>
  );
}

// ─── Seller card ─────────────────────────────────────────────────────────────
function SellerCard({ seller, navigate }) {
  const logo = seller.logo
    ? (seller.logo.startsWith('http') ? seller.logo : `${API_BASE_URL.replace('/api', '')}${seller.logo}`)
    : null;
  return (
    <div className="mai-seller-card" onClick={() => navigate(`/supplier/${seller.id}`)}>
      <div className="mai-seller-logo">
        {logo
          ? <img src={logo} alt={seller.companyName || seller.name} />
          : <span className="mai-seller-initial">{(seller.companyName || seller.name || 'S')[0].toUpperCase()}</span>
        }
      </div>
      <div className="mai-seller-body">
        <p className="mai-seller-name">{seller.companyName || seller.name || 'Supplier'}</p>
        {seller.country && <p className="mai-seller-country">{seller.country}</p>}
        {seller.memberYears && <p className="mai-seller-years">{seller.memberYears} yrs on platform</p>}
      </div>
      <span className="mai-seller-badge">Verified</span>
    </div>
  );
}

// ─── AI Message bubble ───────────────────────────────────────────────────────
function AIBubble({ msg, navigate }) {
  const r = msg.response;
  if (!r) return null;

  const renderText = (text) =>
    (text || '').split('**').map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );

  return (
    <div className="mai-bubble ai">
      <div className="mai-bubble-avatar">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#ff6600" opacity="0.15"/>
          <path d="M12 2a10 10 0 1 1 0 20A10 10 0 0 1 12 2zm0 2a8 8 0 1 0 0 16A8 8 0 0 0 12 4zm-1 4h2v5h-2V8zm0 6h2v2h-2v-2z" fill="#ff6600"/>
        </svg>
      </div>
      <div className="mai-bubble-content">
        <p className="mai-bubble-text">{renderText(r.text)}</p>

        {r.type === 'products' && r.items?.length > 0 && (
          <div className="mai-result-grid">
            {r.items.map(p => <ProductCard key={p.id} product={p} navigate={navigate} />)}
          </div>
        )}

        {r.type === 'sellers' && r.items?.length > 0 && (
          <div className="mai-result-list">
            {r.items.map(s => <SellerCard key={s.id} seller={s} navigate={navigate} />)}
          </div>
        )}

        {r.type === 'market' && (
          <>
            <div className="mai-stats-grid">
              {r.stats.map((s, i) => (
                <div key={i} className="mai-stat-item">
                  <span className="mai-stat-val">{s.value}</span>
                  <span className="mai-stat-lbl">{s.label}</span>
                </div>
              ))}
            </div>
            {r.products?.length > 0 && (
              <div className="mai-result-grid">
                {r.products.map(p => <ProductCard key={p.id} product={p} navigate={navigate} />)}
              </div>
            )}
          </>
        )}

        {r.type === 'design' && (
          <>
            <ul className="mai-ideas-list">
              {r.ideas.map((idea, i) => (
                <li key={i}>{idea}</li>
              ))}
            </ul>
            {r.products?.length > 0 && (
              <div className="mai-result-grid">
                {r.products.map(p => <ProductCard key={p.id} product={p} navigate={navigate} />)}
              </div>
            )}
          </>
        )}

        {r.type === 'trends' && (
          <>
            <div className="mai-trend-chips">
              {r.categories.map((cat, i) => (
                <span key={i} className="mai-trend-chip" onClick={() => navigate(`/products?category=${encodeURIComponent(cat)}`)}>{cat}</span>
              ))}
            </div>
            {r.products?.length > 0 && (
              <div className="mai-result-grid">
                {r.products.map(p => <ProductCard key={p.id} product={p} navigate={navigate} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function MobileAIModePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]); // [{type:'user'|'ai', text, response}]
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // refs so sendQuery always has fresh data without stale-closure
  const productsRef = useRef([]);
  const sellersRef = useRef([]);
  const queryRef = useRef('');
  const imagePreviewRef = useRef(null);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // keep refs in sync
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { sellersRef.current = sellers; }, [sellers]);
  useEffect(() => { queryRef.current = query; }, [query]);
  useEffect(() => { imagePreviewRef.current = imagePreview; }, [imagePreview]);

  // Load data once
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      productsApi.getAll({ limit: 200 }).catch((e) => { console.error('[AI] products load err:', e); return []; }),
      sellersApi.getAll({ limit: 200 }).catch((e) => { console.error('[AI] sellers load err:', e); return []; }),
    ]).then(([pData, sData]) => {
      if (cancelled) return;
      const toArr = (d) => {
        if (Array.isArray(d)) return d;
        if (d && Array.isArray(d.products)) return d.products;
        if (d && Array.isArray(d.sellers)) return d.sellers;
        if (d && Array.isArray(d.data)) return d.data;
        return [];
      };
      const pArr = toArr(pData);
      const sArr = toArr(sData);
      setProducts(pArr);
      setSellers(sArr);
      productsRef.current = pArr;
      sellersRef.current = sArr;
      setDataReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendQuery = useCallback(async (text) => {
    const q = (typeof text === 'string' ? text : queryRef.current).trim();
    const imgPrev = imagePreviewRef.current;
    if (!q && !imgPrev) return;

    const userMsg = { type: 'user', text: q || '📷 Image uploaded for search', image: imgPrev };
    setMessages(prev => [...prev, userMsg]);
    setQuery('');
    queryRef.current = '';
    setImageFile(null);
    setImagePreview(null);
    imagePreviewRef.current = null;
    setLoading(true);

    try {
      // always use refs — never stale
      const response = getAIResponse(q, productsRef.current, sellersRef.current);
      setMessages(prev => [...prev, { type: 'ai', response }]);
    } catch (err) {
      console.error('[AI] sendQuery error:', err);
      setMessages(prev => [...prev, { type: 'ai', response: { type: 'text', text: 'I had trouble processing that. Please try again.' } }]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChip = (chip) => {
    const q = CHIP_QUERIES[chip] || chip;
    sendQuery(q);
  };

  // Voice input
  const handleMic = () => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) {
      alert('Voice input is not supported in your browser.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SpeechRec();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery(transcript);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  // Camera / image input
  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuery();
    }
  };

  const isLanding = messages.length === 0;

  return (
    <div className="mai-page">
      {/* ── Header ── */}
      <div className="mai-header">
        <div className="mai-header-top">
          <button className="mai-header-back" onClick={() => navigate('/')}>
            {/* history icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-4.95"/>
            </svg>
          </button>
          <div className="mai-header-right">
            <span className="mai-tasks-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 17h7M17.5 14v7"/></svg>
              10
            </span>
            <button className="mai-upgrade-btn" onClick={() => navigate('/membership-packages')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff6600"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              Upgrade
            </button>
          </div>
        </div>

        {/* Tab nav */}
        <div className="mai-tabs">
          {['AI Mode', 'Products', 'Manufacturers', 'Worldwide'].map(tab => (
            <button
              key={tab}
              className={`mai-tab ${tab === 'AI Mode' ? 'active' : ''}`}
              onClick={() => {
                if (tab === 'Products') navigate('/');
                else if (tab === 'Manufacturers') navigate('/', { state: { mobileNavTab: 'Manufacturers' } });
                else if (tab === 'Worldwide') navigate('/mobile/worldwide');
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ── Chat area ── */}
      <div className="mai-chat">
        {isLanding ? (
          <div className="mai-landing">
            {/* Try free banner */}
            <div className="mai-free-banner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff6600"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              Try now! Get 10 tasks a day for free.
            </div>

            {/* Headline */}
            <h2 className="mai-headline">
              Smart sourcing with <span className="mai-headline-accent">AI Mode</span>
            </h2>

            {/* Suggested prompt cards */}
            <div className="mai-suggested">
              {SUGGESTED.map(s => (
                <button key={s.id} className="mai-suggested-card" onClick={() => sendQuery(s.sub)}>
                  <div className="mai-suggested-content">
                    <span className="mai-suggested-title">{s.title}</span>
                    <p className="mai-suggested-sub">{s.sub}</p>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              ))}
            </div>

            {/* Feature chips */}
            <div className="mai-chips">
              {CHIPS.map(chip => (
                <button key={chip} className="mai-chip" onClick={() => handleChip(chip)}>
                  {chip}
                </button>
              ))}
            </div>

            {/* Scroll hint */}
            <div className="mai-scroll-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
              Scroll down to see more
            </div>
          </div>
        ) : (
          <div className="mai-messages">
            {messages.map((msg, i) => (
              <React.Fragment key={i}>
                {msg.type === 'user' && (
                  <div className="mai-bubble user">
                    {msg.image && (
                      <img src={msg.image} alt="upload" className="mai-bubble-img" />
                    )}
                    {msg.text && <p className="mai-bubble-text">{msg.text}</p>}
                  </div>
                )}
                {msg.type === 'ai' && <AIBubble msg={msg} navigate={navigate} />}
              </React.Fragment>
            ))}
            {loading && (
              <div className="mai-bubble ai">
                <div className="mai-bubble-avatar">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#ff6600" opacity="0.15"/><path d="M12 2a10 10 0 1 1 0 20A10 10 0 0 1 12 2zm-1 5h2v7h-2V7zm0 8h2v2h-2v-2z" fill="#ff6600"/></svg>
                </div>
                <div className="mai-bubble-content">
                  <div className="mai-typing">
                    <span/><span/><span/>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      {/* ── Image preview strip ── */}
      {imagePreview && (
        <div className="mai-img-preview">
          <img src={imagePreview} alt="preview" />
          <button onClick={() => { setImageFile(null); setImagePreview(null); }}>×</button>
        </div>
      )}

      {/* ── Input box ── */}
      <div className="mai-input-wrap">
        <div className="mai-input-box">
          <textarea
            ref={inputRef}
            className="mai-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your needs..."
            rows={1}
          />
          <div className="mai-input-actions">
            {/* Camera */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            <button className="mai-action-btn" onClick={handleCameraClick} aria-label="Upload image">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <rect x="2" y="5" width="20" height="15" rx="2"/>
                <circle cx="12" cy="12.5" r="3.5"/>
                <rect x="6" y="2" width="4" height="3" rx="1"/>
              </svg>
            </button>

            {/* Send / Mic */}
            {query.trim() || imagePreview ? (
              <button className="mai-send-btn" onClick={() => sendQuery()} aria-label="Send">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            ) : (
              <button
                className={`mai-action-btn ${listening ? 'mai-listening' : ''}`}
                onClick={handleMic}
                aria-label="Voice input"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={listening ? '#ff6600' : 'currentColor'} strokeWidth="1.6">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom Nav ── */}
      <MobileBottomNav />
    </div>
  );
}
